<template>
  <div class="chapter-notes">
    <template v-if="parsed.ok">
      <div class="chapter-strip">
        <div class="chapter-strip__filters">
          <button class="filter-pill" :class="{ 'filter-pill--active': filter === 'all' }" @click="filter = 'all'">
            All Chapters
          </button>
          <button
            v-if="chaptersWithCritical > 0"
            class="filter-pill"
            :class="{ 'filter-pill--active': filter === 'critical' }"
            @click="filter = 'critical'"
          >
            <span class="filter-pill__dot"></span>
            Needs Attention
          </button>
        </div>
        <div class="chapter-strip__cards" ref="stripRef">
          <button
            v-for="chapter in filteredChapters"
            :key="chapter.id"
            class="chapter-card"
            :class="{ 'chapter-card--active': activeChapterId === chapter.id }"
            :data-chapter-card="chapter.id"
            @click="jumpToChapter(chapter.id)"
          >
            <span class="chapter-card__number">Ch. {{ chapter.number }}</span>
            <span v-if="chapter.title" class="chapter-card__title">{{ chapter.title }}</span>
            <span class="chapter-card__dots">
              <span
                v-for="dot in severityDots(chapter)"
                :key="dot.key"
                class="severity-dot"
                :style="{ background: dot.color }"
                :title="dot.title"
              ></span>
            </span>
          </button>
        </div>
      </div>

      <div class="notes-document">
        <div v-if="parsed.intro" class="notes-intro notes-richtext" v-html="render(parsed.intro)"></div>

        <section
          v-for="chapter in parsed.chapters"
          :key="chapter.id"
          :id="chapter.id"
          class="chapter-section"
          :data-chapter-section="chapter.id"
        >
          <h2 class="chapter-section__heading">{{ chapter.heading }}</h2>

          <div v-for="(block, i) in chapter.blocks" :key="`${chapter.id}-block-${i}`" class="note-block">
            <template v-if="isCardBlock(block.type)">
              <div class="note-card" :class="`note-card--${block.type}`">
                <div class="note-card__label">{{ block.label }}</div>
                <div class="note-card__body notes-richtext" v-html="render(block.markdown)"></div>
              </div>
            </template>
            <template v-else>
              <div v-if="block.label" class="note-block__label">{{ block.label }}</div>
              <div class="notes-richtext" v-html="render(block.markdown)"></div>
            </template>
          </div>
        </section>

        <div v-if="parsed.outro" class="notes-outro notes-richtext" v-html="render(parsed.outro)"></div>
      </div>
    </template>

    <!-- Fallback: unparseable content renders exactly like a generic output -->
    <div v-else class="notes-document">
      <div class="notes-richtext" v-html="render(content)"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { parseChapterNotes, type ChapterNote, type NoteBlockType } from '../lib/chapterNotesParser';
import { parseMarkdown } from '../lib/renderOutput';

const props = defineProps<{ content: string }>();

const COLORS = {
  critical: '#1a5f8a',
  important: '#61aed4',
};

const parsed = computed(() => parseChapterNotes(props.content));
const filter = ref<'all' | 'critical'>('all');
const activeChapterId = ref<string>('');
const stripRef = ref<HTMLElement>();

const chaptersWithCritical = computed(() => parsed.value.chapters.filter(c => c.severity.critical > 0).length);

const filteredChapters = computed(() =>
  filter.value === 'critical' ? parsed.value.chapters.filter(c => c.severity.critical > 0) : parsed.value.chapters
);

function render(markdown: string): string {
  return parseMarkdown(markdown);
}

function isCardBlock(type: NoteBlockType): boolean {
  return type === 'strength' || type === 'critical' || type === 'important';
}

function severityDots(chapter: ChapterNote) {
  // One dot per severity present; strengths intentionally not shown
  const dots: Array<{ key: string; color: string; title: string }> = [];
  if (chapter.severity.critical > 0) {
    dots.push({ key: 'critical', color: COLORS.critical, title: `${chapter.severity.critical} critical areas` });
  }
  if (chapter.severity.important > 0) {
    dots.push({
      key: 'important',
      color: COLORS.important,
      title: `${chapter.severity.important} enhancement opportunities`,
    });
  }
  return dots;
}

// Scrolls only the strip. Element.scrollIntoView would also scroll the page,
// which cancels any smooth scroll already running there.
function revealCard(id: string) {
  const strip = stripRef.value;
  const card = strip?.querySelector(`[data-chapter-card="${id}"]`);
  if (!strip || !card) return;
  const stripBox = strip.getBoundingClientRect();
  const cardBox = card.getBoundingClientRect();
  const offset =
    cardBox.left < stripBox.left
      ? cardBox.left - stripBox.left
      : cardBox.right > stripBox.right
        ? cardBox.right - stripBox.right
        : 0;
  if (offset) strip.scrollBy({ left: offset, behavior: 'smooth' });
}

// A jump passes through every chapter on the way, so scroll-spy stays off until
// the page stops. The timer covers a jump with nowhere to scroll and browsers
// without scrollend.
const JUMP_SETTLE_MS = 400;
let jumping = false;
let jumpTimer: ReturnType<typeof setTimeout> | undefined;

function extendJump() {
  clearTimeout(jumpTimer);
  jumpTimer = setTimeout(endJump, JUMP_SETTLE_MS);
}

function endJump() {
  clearTimeout(jumpTimer);
  jumping = false;
  document.removeEventListener('scroll', extendJump, true);
  document.removeEventListener('scrollend', handleScrollEnd, true);
}

// The strip and the window scroll during a jump too; only the pane holding the
// chapter counts
function handleScrollEnd(e: Event) {
  const section = document.getElementById(activeChapterId.value);
  if (e.target instanceof Element && e.target.contains(section)) endJump();
}

function jumpToChapter(id: string) {
  activeChapterId.value = id;
  revealCard(id);
  jumping = true;
  // Neither event bubbles, so catch the content pane's in the capture phase
  document.addEventListener('scroll', extendJump, true);
  document.addEventListener('scrollend', handleScrollEnd, true);
  extendJump();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Scroll-spy: highlight the chapter card for the section nearest the viewport top
let observer: IntersectionObserver | null = null;

function observeSections() {
  observer?.disconnect();
  observer = new IntersectionObserver(
    entries => {
      if (jumping) return;
      const visible = entries.filter(e => e.isIntersecting);
      if (visible.length === 0) return;
      const top = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
      const id = top.target.getAttribute('data-chapter-section');
      if (id) {
        activeChapterId.value = id;
        revealCard(id);
      }
    },
    { rootMargin: '-10% 0px -70% 0px' }
  );
  document.querySelectorAll('[data-chapter-section]').forEach(el => observer!.observe(el));
}

onMounted(observeSections);
watch(
  () => parsed.value.chapters.length,
  () => setTimeout(observeSections, 0)
);
onBeforeUnmount(() => {
  observer?.disconnect();
  endJump();
});
</script>

<style scoped>
.chapter-notes {
  max-width: 816px;
  margin: 0 auto;
}

/* ─── Sticky chapter index strip ─── */

.chapter-strip {
  position: sticky;
  top: -2rem;
  z-index: 10;
  margin: -2rem -1rem 1.5rem;
  padding: 1rem 1rem 0.75rem;
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border, #e2e5e9);
}

@media (max-width: 768px) {
  .chapter-strip {
    top: -0.5rem;
    margin: -0.5rem -0.5rem 1rem;
    padding: 0.5rem 0.5rem 0.5rem;
  }
}

.chapter-strip__filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--color-border, #d0d7de);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-pill--active {
  background: white;
  color: var(--color-text-primary);
  border-color: #61aed4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filter-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #1a5f8a;
}

.chapter-strip__cards {
  display: flex;
  gap: 0.6rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scrollbar-width: thin;
}

.chapter-strip__cards::-webkit-scrollbar {
  height: 6px;
}

.chapter-strip__cards::-webkit-scrollbar-thumb {
  background: var(--color-border, #d0d7de);
  border-radius: 3px;
}

.chapter-card {
  flex: 0 0 140px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border, #d0d7de);
  border-top: 3px solid transparent;
  background: var(--color-bg-tertiary, #f3f4f6);
  cursor: pointer;
  text-align: left;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.chapter-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
}

.chapter-card--active {
  background: white;
  border-top-color: #61aed4;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.chapter-card__number {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.chapter-card--active .chapter-card__number {
  color: #61aed4;
}

.chapter-card__title {
  font-family: 'PT Serif', serif;
  font-style: italic;
  font-size: 0.85rem;
  line-height: 1.25;
  color: var(--color-text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chapter-card--active .chapter-card__title {
  font-weight: 700;
}

.chapter-card__dots {
  display: flex;
  gap: 4px;
}

.severity-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

@media (max-width: 768px) {
  .chapter-card {
    flex-basis: 110px;
    height: 84px;
  }
}

/* ─── Paper document ─── */

.notes-document {
  background: white;
  border-radius: var(--border-radius, 8px);
  box-shadow: var(--shadow-lg, 0 10px 25px -5px rgba(0, 0, 0, 0.08));
  padding: 72px;
}

@media (max-width: 1040px) {
  .notes-document {
    padding: 48px;
    box-shadow: var(--shadow-md);
  }
}

@media (max-width: 768px) {
  .notes-document {
    padding: 24px;
  }
}

.notes-intro {
  font-style: italic;
  color: var(--color-text-secondary);
  margin-bottom: 2rem;
}

.notes-outro {
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid var(--color-border, #e2e5e9);
}

.chapter-section {
  padding-top: 1rem;
  scroll-margin-top: 140px;
}

.chapter-section + .chapter-section {
  margin-top: 2.5rem;
  border-top: 1px solid var(--color-border, #e2e5e9);
  padding-top: 2.5rem;
}

.chapter-section__heading {
  font-family: 'PT Serif', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 1.25rem;
}

.note-block {
  margin-bottom: 1.5rem;
}

.note-block__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
}

/* ─── Colored cards (blue ramp, matching the editorial letter) ─── */

.note-card {
  border-left: 4px solid;
  border-radius: 0 8px 8px 0;
  padding: 1.1rem 1.25rem;
}

.note-card__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.note-card--strength {
  border-left-color: #2d9e5f;
  background: #f2faf5;
}

.note-card--strength .note-card__label {
  color: #2d9e5f;
}

.note-card--critical {
  border-left-color: #1a5f8a;
  background: #f0f7fc;
}

.note-card--critical .note-card__label {
  color: #1a5f8a;
}

.note-card--important {
  border-left-color: #61aed4;
  background: #f5fafd;
}

.note-card--important .note-card__label {
  color: #4a94ba;
}

/* ─── Markdown typography (mirrors ContentArea's .rich-text) ─── */

.notes-richtext {
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.75;
  color: var(--color-text-primary);
}

.notes-richtext :deep(p) {
  margin-bottom: 1rem;
}

.notes-richtext :deep(p:last-child) {
  margin-bottom: 0;
}

.notes-richtext :deep(strong) {
  font-weight: 600;
}

.notes-richtext :deep(em) {
  font-style: italic;
}

.notes-richtext :deep(ul),
.notes-richtext :deep(ol) {
  margin: 0.5rem 0 1rem 1.5rem;
}

.notes-richtext :deep(li) {
  margin-bottom: 0.5rem;
}

.notes-richtext :deep(ul li) {
  list-style: disc;
}

.notes-richtext :deep(ol li) {
  list-style: decimal;
}

.notes-richtext :deep(h1),
.notes-richtext :deep(h2),
.notes-richtext :deep(h3),
.notes-richtext :deep(h4) {
  font-weight: 600;
  margin: 1.5rem 0 0.75rem;
  line-height: 1.3;
}

.notes-richtext :deep(h3) {
  font-size: 1.15rem;
}

/* Model output often includes --- breaks; chapter sections have their own rules */
.notes-richtext :deep(hr) {
  display: none;
}
</style>
